/*
 OK FBI — Intermission (Sponsor Break) system
 Two exports:
   - IntermissionPanel: host controls (Dashboard)
   - IntermissionOverlay: full-screen premium break screen rendered on the OBS Overlay page

 Overlay hides all case content during break — the backend already returns a stripped
 payload while intermission is active. On end, the previous OBS preset is restored.
*/
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { assetUrl, useSession } from "@/contexts/SessionContext";
import { Pause, Play, Coffee, RotateCcw, Clock3 } from "lucide-react";
import { toast } from "sonner";

const clock = (s) => {
  s = Math.max(0, Math.floor(s));
  const m = Math.floor(s / 60), sec = s % 60;
  return `${String(m).padStart(2, "0")}:${String(sec).padStart(2, "0")}`;
};

// ============ Host Panel (Dashboard) ============
export function IntermissionPanel() {
  const [state, setState] = useState({ active: false });
  const [custom, setCustom] = useState(5);
  const [title, setTitle] = useState("استراحة التحقيق");
  const [subtitle, setSubtitle] = useState("سنعود بعد قليل");
  const [showCase, setShowCase] = useState(true);
  const [now, setNow] = useState(Date.now());

  const load = useCallback(async () => {
    try { const { data } = await api.get("/session/intermission"); setState(data || { active: false }); } catch {}
  }, []);
  useEffect(() => { load(); const iv = setInterval(load, 3000); return () => clearInterval(iv); }, [load]);
  useEffect(() => { const t = setInterval(() => setNow(Date.now()), 1000); return () => clearInterval(t); }, []);

  const start = async (duration_sec) => {
    try {
      const { data } = await api.post("/session/intermission/start", { duration_sec, title, subtitle, show_case: showCase });
      setState(data.intermission);
      toast.success("بدأت استراحة التحقيق");
    } catch { toast.error("تعذّر بدء الاستراحة"); }
  };
  const end = async () => {
    try {
      const { data } = await api.post("/session/intermission/end");
      setState({ active: false });
      toast.success(data.restored_preset ? "عاد البث إلى التحقيق" : "انتهت الاستراحة");
    } catch { toast.error("تعذّر إنهاء الاستراحة"); }
  };

  const remaining = useMemo(() => {
    if (!state?.active || !state?.started_at) return 0;
    const started = new Date(state.started_at).getTime();
    const left = (state.duration_sec || 0) - Math.floor((now - started) / 1000);
    return Math.max(0, left);
  }, [state, now]);

  const isRunning = state?.active;

  return (
    <div className="panel p-4 border-metal" data-testid="intermission-panel">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2"><Coffee size={14} className="text-gold" /><div className="label-caps">استراحة التحقيق</div></div>
        <div className="text-[9px] text-zinc-600 font-mono-ar">INTERMISSION</div>
      </div>

      {isRunning ? (
        <>
          <div className="border border-gold bg-gradient-to-b from-[rgba(197,160,89,0.08)] to-transparent p-3 mb-3">
            <div className="flex items-center justify-between">
              <div className="text-[10px] font-mono-ar text-zinc-400">الوقت المتبقي</div>
              <div className="font-mono-ar tabular-nums text-3xl text-gold" data-testid="intermission-remaining">{clock(remaining)}</div>
            </div>
            <div className="text-[11px] text-zinc-300 mt-1 font-cairo">{state?.title || "استراحة التحقيق"}</div>
            <div className="text-[10px] text-zinc-500 mt-0.5 font-cairo">{state?.subtitle || "سنعود بعد قليل"}</div>
          </div>
          <button onClick={end} data-testid="intermission-end"
            className="w-full btn-primary py-2 flex items-center justify-center gap-2">
            <Play size={13} /> العودة للتحقيق
          </button>
          <div className="text-[9px] text-zinc-600 mt-2 leading-tight">
            سيتم استعادة قالب OBS السابق تلقائياً · لا يتم إعادة تعيين حالة القضية.
          </div>
        </>
      ) : (
        <>
          <div className="grid grid-cols-2 gap-2 mb-2">
            <div>
              <label className="text-[9px] font-mono-ar text-zinc-500 mb-0.5 block">العنوان الرئيسي</label>
              <input value={title} onChange={(e) => setTitle(e.target.value)} data-testid="intermission-title"
                className="w-full bg-black/40 border border-metal px-2 py-1 text-[11px] text-white outline-none focus:border-gold" maxLength={60} />
            </div>
            <div>
              <label className="text-[9px] font-mono-ar text-zinc-500 mb-0.5 block">العنوان الفرعي</label>
              <input value={subtitle} onChange={(e) => setSubtitle(e.target.value)} data-testid="intermission-subtitle"
                className="w-full bg-black/40 border border-metal px-2 py-1 text-[11px] text-white outline-none focus:border-gold" maxLength={120} />
            </div>
          </div>
          <label className="flex items-center gap-2 text-[11px] text-zinc-400 mb-3 cursor-pointer">
            <input type="checkbox" checked={showCase} onChange={(e) => setShowCase(e.target.checked)} data-testid="intermission-show-case" className="accent-yellow-600" />
            <span>عرض اسم القضية على شاشة الاستراحة</span>
          </label>
          <div className="grid grid-cols-3 gap-2 mb-2" data-testid="intermission-durations">
            <button onClick={() => start(5 * 60)} data-testid="intermission-start-5" className="btn-primary text-[12px] py-1.5">5 دقائق</button>
            <button onClick={() => start(10 * 60)} data-testid="intermission-start-10" className="btn-primary text-[12px] py-1.5">10 دقائق</button>
            <button onClick={() => start(15 * 60)} data-testid="intermission-start-15" className="btn-primary text-[12px] py-1.5">15 دقيقة</button>
          </div>
          <div className="flex items-center gap-2 mt-2">
            <label className="text-[10px] text-zinc-500 font-mono-ar">مدة مخصصة (دقائق):</label>
            <input type="number" min={1} max={120} value={custom}
              onChange={(e) => setCustom(Math.max(1, Math.min(120, Number(e.target.value) || 1)))}
              data-testid="intermission-custom-input"
              className="w-16 bg-black/40 border border-metal px-2 py-1 text-[12px] text-white outline-none focus:border-gold tabular-nums" />
            <button onClick={() => start(Math.max(1, custom) * 60)} data-testid="intermission-start-custom"
              className="text-[11px] px-3 py-1 border border-gold text-gold hover:bg-[rgba(197,160,89,0.15)] flex items-center gap-1">
              <Clock3 size={11} /> بدء
            </button>
          </div>
          <div className="text-[9px] text-zinc-600 mt-3 leading-tight">
            أثناء الاستراحة يتم إخفاء جميع الأدلة/المشتبه بهم/التصويت من واجهة البث تلقائياً.
          </div>
        </>
      )}
    </div>
  );
}

// ============ Overlay Full-Screen Break Screen ============
// Premium 3D-styled OK FBI emblem, subtle ambient movement, muted metallic accents.
// Rendered on the OBS Overlay page ONLY when state.intermission.active is true.
export function IntermissionOverlay({ intermission, session }) {
  const [now, setNow] = useState(Date.now());
  useEffect(() => { const t = setInterval(() => setNow(Date.now()), 500); return () => clearInterval(t); }, []);
  const started = intermission?.started_at ? new Date(intermission.started_at).getTime() : Date.now();
  const total = intermission?.duration_sec || 0;
  const passed = Math.floor((now - started) / 1000);
  const remaining = Math.max(0, total - passed);
  const pct = total > 0 ? Math.max(0, Math.min(100, (remaining / total) * 100)) : 0;

  return (
    <div
      dir="rtl"
      data-testid="overlay-intermission"
      className="fixed inset-0 flex items-center justify-center overflow-hidden pointer-events-none"
      style={{
        background: "radial-gradient(ellipse at center, rgba(28,20,10,0.35) 0%, rgba(0,0,0,0.94) 65%, #000 100%)",
      }}
    >
      {/* Ambient grain + slow drift layer */}
      <style>{`
        @keyframes intermission-drift { 0%,100%{ transform: translateY(0) scale(1); } 50%{ transform: translateY(-8px) scale(1.02); } }
        @keyframes intermission-halo { 0%,100%{ opacity: 0.35; transform: scale(1);} 50%{ opacity: 0.55; transform: scale(1.05);} }
        @keyframes intermission-scan { 0%{ transform: translateY(-100%);} 100%{ transform: translateY(100%);} }
        .intermission-emblem-wrap { animation: intermission-drift 12s ease-in-out infinite; }
        .intermission-halo { animation: intermission-halo 6s ease-in-out infinite; }
        .intermission-noise { background-image: radial-gradient(rgba(255,255,255,0.02) 1px, transparent 1px); background-size: 3px 3px; }
      `}</style>
      {/* Ambient halo behind emblem */}
      <div className="absolute intermission-halo" style={{
        width: 720, height: 720, borderRadius: "50%",
        background: "radial-gradient(circle, rgba(197,160,89,0.22) 0%, rgba(197,160,89,0.05) 45%, transparent 70%)",
        filter: "blur(20px)"
      }} />
      {/* Subtle vertical scanline (very slow) */}
      <div className="absolute inset-0 pointer-events-none opacity-40" style={{
        background: "linear-gradient(180deg, transparent 0%, rgba(197,160,89,0.06) 50%, transparent 100%)",
        height: "40%", animation: "intermission-scan 14s linear infinite"
      }} />
      {/* Grain */}
      <div className="absolute inset-0 intermission-noise pointer-events-none" />

      {/* Center content */}
      <div className="relative flex flex-col items-center text-center px-8" style={{ maxWidth: 900 }}>
        <div className="intermission-emblem-wrap" style={{
          filter: "drop-shadow(0 20px 40px rgba(0,0,0,0.85)) drop-shadow(0 6px 12px rgba(197,160,89,0.35))"
        }}>
          <img
            src={assetUrl("/generated_images/okfbi-emblem-4k-transparent.png")}
            alt="OK FBI"
            className="w-[300px] h-[300px] object-contain"
            style={{ background: "transparent" }}
            draggable={false}
            data-testid="intermission-emblem"
            onError={(e) => {
              if (!e.target.dataset.triedFallback) {
                e.target.dataset.triedFallback = "1";
                e.target.src = "/generated_images/okfbi-emblem-4k-transparent.png";
              }
            }}
          />
        </div>
        {/* Divider — metallic ribbon */}
        <div className="mt-6 mb-4 flex items-center gap-3 w-[520px] max-w-full">
          <div className="flex-1 h-px" style={{ background: "linear-gradient(90deg, transparent, rgba(197,160,89,0.7), transparent)" }} />
          <div className="text-[10px] tracking-[0.6em] font-mono-ar text-gold">OK FBI · LIVE</div>
          <div className="flex-1 h-px" style={{ background: "linear-gradient(90deg, transparent, rgba(197,160,89,0.7), transparent)" }} />
        </div>
        {/* Titles */}
        <div className="font-cairo text-[56px] leading-none text-white tracking-wide"
             style={{ textShadow: "0 4px 20px rgba(0,0,0,0.85), 0 0 2px rgba(197,160,89,0.35)" }}
             data-testid="intermission-title-text">
          {intermission?.title || "استراحة التحقيق"}
        </div>
        <div className="font-cairo text-[22px] text-zinc-300 mt-3" data-testid="intermission-subtitle-text">
          {intermission?.subtitle || "سنعود بعد قليل"}
        </div>
        {intermission?.case?.title && (
          <div className="mt-6 border border-gold/40 px-4 py-1.5 bg-black/40 backdrop-blur-sm">
            <div className="text-[10px] font-mono-ar text-gold tracking-[0.35em]">CASE {intermission.case.code}</div>
            <div className="text-[13px] text-zinc-200 font-cairo">{intermission.case.title}</div>
          </div>
        )}
        {/* Countdown */}
        <div className="mt-8 w-[520px] max-w-full" data-testid="intermission-countdown">
          <div className="flex items-center justify-between text-[10px] font-mono-ar text-zinc-400 tracking-[0.35em] mb-1.5">
            <span>REMAINING</span>
            <span>{clock(remaining)}</span>
          </div>
          <div className="h-1.5 bg-black/60 border border-metal/40 relative overflow-hidden">
            <div className="absolute inset-y-0 right-0" style={{
              width: `${pct}%`,
              background: "linear-gradient(90deg, #7a5a1c, #C5A059, #E8D08A, #C5A059)",
              transition: "width 500ms linear",
              boxShadow: "0 0 12px rgba(197,160,89,0.5)",
            }} />
          </div>
        </div>
      </div>
      {/* Corner watermark */}
      <div className="absolute bottom-3 right-4 text-[9px] tracking-[0.3em] text-zinc-500/70 font-mono-ar">OK FBI · INTERMISSION</div>
    </div>
  );
}
